import { getSsoBootstrapToken, exchangeSsoForGraphToken, SsoExchangeError } from "./ssoAuth";
import { signInWithDialog } from "./msalDialogAuth";
import { acquireMsalTokenSilent, clearMsalCache } from "./msalClient";
import { cacheToken, getCachedToken, clearCachedToken } from "./tokenCache";
import {
  recordSilentFailure,
  clearConsentRequired,
  recordConsentRequired,
} from "./authDiagnostics";

/* global OfficeRuntime */

// 13008 means another getAccessToken call is already in progress — transient, so
// the caller should retry rather than open a sign-in dialog. Every other Office
// identity error means SSO cannot help at all, and interactive MSAL is the only
// way through. See SSO_ERROR_LABELS in authDiagnostics for what each code means.
const SSO_NO_FALLBACK_CODES = new Set([13008]);

/**
 * Acquires a Microsoft Graph access token, preferring Office SSO + OBO and
 * falling back to interactive MSAL sign-in for personal accounts or when SSO
 * cannot obtain consent. REQ-AUTH-001, REQ-AUTH-002.
 */
export async function acquireGraphToken(): Promise<string> {
  const token = await acquireGraphTokenInteractive();
  // Any token at all means consent is not the obstacle — including one obtained
  // through the MSAL dialog, where the user consented for themselves and never
  // went back through the OBO exchange that would otherwise clear this.
  clearConsentRequired();
  cacheToken(token);
  return token;
}

async function acquireGraphTokenInteractive(): Promise<string> {
  try {
    const ssoToken = await getSsoBootstrapToken();
    return await exchangeSsoForGraphToken(ssoToken);
  } catch (err) {
    // 13005 is Office refusing because consent is missing, which happens before
    // the OBO exchange that normally detects this — so without it here, a tenant
    // that has never approved the add-in gets no consent guidance at all. No SSO
    // token means no tid claim, so the link falls back to /organizations, which
    // applies to whichever tenant the administrator signs in with.
    if ((err as { code?: number }).code === 13005) {
      recordConsentRequired();
    }
    if (shouldFallbackToInteractive(err)) {
      return signInWithDialog();
    }
    throw err;
  }
}

function shouldFallbackToInteractive(err: unknown): boolean {
  // OBO reported consent/interaction required.
  if (err instanceof SsoExchangeError) {
    return true;
  }
  // Any Office identity-API failure other than a transient in-progress error.
  const code = (err as { code?: number }).code;
  return typeof code === "number" && !SSO_NO_FALLBACK_CODES.has(code);
}

/**
 * Attempts to restore an existing session without any user interaction, so the
 * user stays signed in across task pane reopens and Outlook restarts.
 * Returns a Graph token if one can be obtained silently, otherwise null.
 * REQ-AUTH-003, REQ-AUTH-005.
 */
export async function acquireGraphTokenSilent(): Promise<string | null> {
  // 1. A still-valid token from a previous sign-in (works for any account type,
  //    no network, no iframe — reliable inside the add-in sandbox). Storage may
  //    be unavailable or ephemeral in a third-party iframe, so a miss here is
  //    expected rather than fatal: fall through to SSO, which needs no storage.
  try {
    const cached = getCachedToken();
    if (cached) {
      return cached;
    }
  } catch (err) {
    recordSilentFailure("cache", err);
  }

  // 2. Office SSO (work/school): silent — no sign-in or consent prompts. This
  //    is the path that keeps a work account signed in when nothing persists.
  try {
    const ssoToken = await OfficeRuntime.auth.getAccessToken({
      allowSignInPrompt: false,
      allowConsentPrompt: false,
    });
    const token = await exchangeSsoForGraphToken(ssoToken);
    cacheToken(token);
    return token;
  } catch (err) {
    recordSilentFailure("sso", err);
    // 3. Personal accounts: fall back to a cached MSAL token if one exists.
    try {
      const token = await acquireMsalTokenSilent();
      if (token) {
        cacheToken(token);
      }
      return token;
    } catch (msalErr) {
      recordSilentFailure("msal", msalErr);
      return null;
    }
  }
}

/** Signs the user out and clears the cached session. REQ-AUTH-009. */
export async function signOut(): Promise<void> {
  clearCachedToken();
  await clearMsalCache();
}
